import React, { useState, useMemo } from 'react';
import Card from '../components/ui/Card';
import { useAppContext } from '../contexts/AppContext';
import { Layers, FileText, Files, Info, AlertTriangle } from 'lucide-react';
import { ModuleInfoModal } from '../components/ModuleInfoModal';
import { normalizeProjectName } from '../utils/validation';
import ActiveProjectBadge from '../components/ActiveProjectBadge';
import { getStandardConfig, RiskLevel, RiskMatrixConfig } from '../constants/fmeaStandards';
import { FmeaStandard } from '../types';

interface RiskDataPoint {
    x: number;
    y: number;
    id: string;
    rpn: number;
    riskLevelIndex: number;
}

interface RiskMatrixProps {
    title: string;
    xAxisLabel: string;
    yAxisLabel: string;
    data: RiskDataPoint[];
    riskCounts: Record<number, number>;
    riskConfig: RiskMatrixConfig;
    levels: RiskLevel[];
}

type FilterMode = 'active' | 'draft' | 'all';

const RiskMatrix: React.FC<RiskMatrixProps> = ({ title, xAxisLabel, yAxisLabel, data, riskCounts, riskConfig, levels }) => {
    const { navigate, highlightedItem } = useAppContext();
    const gridSize = 10;

    // Sort items by RPN descending to get rank within the visible set
    const sortedRanks = useMemo(() => {
        return [...data].sort((a, b) => b.rpn - a.rpn)
            .reduce((acc, item, index) => {
                acc[item.id] = index + 1;
                return acc;
            }, {} as Record<string, number>);
    }, [data]);

    const getCellBgColor = (riskLevelIndex: number) => {
        const level = levels[riskLevelIndex];
        if (!level) return 'bg-gray-100';
        return level.bgClass;
    };

    const getCellRiskLevel = (y: number, x: number): number => {
        // Use the standard's risk calculation
        return riskConfig.getRiskLevel(y, x, 5); // Use middle detection for cell coloring
    };

    return (
        <Card>
            <div className="flex justify-between items-start mb-4">
                <div>
                    <h3 className="text-lg font-bold text-text-primary">{title}</h3>
                    <p className="text-xs text-text-secondary mt-1">{riskConfig.thresholdInfo}</p>
                </div>
                <div className="flex gap-1 flex-wrap justify-end">
                    {levels.map((level, idx) => (
                        <div
                            key={idx}
                            className={`flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-bold ${level.bgClass} ${level.textClass} ${level.borderClass}`}
                            title={level.description}
                        >
                            <span className={`w-2 h-2 rounded-full ${level.color === 'red' ? 'bg-red-500' : level.color === 'orange' ? 'bg-orange-500' : level.color === 'yellow' ? 'bg-yellow-500' : 'bg-green-500'}`}></span>
                            {level.name}: {riskCounts[idx] || 0}
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex">
                <div className="flex items-center justify-center -rotate-90 my-auto" style={{ width: '2.5rem', marginLeft: '-1.5rem', marginRight: '0.5rem' }}>
                    <span className="text-sm font-bold tracking-wider text-text-secondary whitespace-nowrap">{yAxisLabel}</span>
                </div>
                <div className="grid grid-cols-10 grid-rows-10 gap-px flex-1 aspect-square border border-border bg-border">
                    {Array.from({ length: gridSize * gridSize }).map((_, index) => {
                        const x = (index % gridSize) + 1;
                        const y = gridSize - Math.floor(index / gridSize);
                        const itemsInCell = data.filter(d => d.x === x && d.y === y);
                        const riskLevel = getCellRiskLevel(y, x);

                        return (
                            <div
                                key={`${x}-${y}`}
                                className={`w-full h-full flex items-center justify-center p-0.5 ${getCellBgColor(riskLevel)}`}
                                title={levels[riskLevel]?.description || ''}
                            >
                                <div className="flex flex-wrap gap-0.5 justify-center content-center w-full h-full overflow-hidden">
                                    {itemsInCell.map(item => {
                                        const isHighlighted = highlightedItem?.type === 'risk-matrix' && highlightedItem.id === item.id;
                                        return (
                                            <button
                                                key={item.id}
                                                title={`ID: ${item.id}\nRPN: ${item.rpn}\nRisk: ${levels[item.riskLevelIndex]?.name || 'Unknown'}`}
                                                onClick={() => navigate('/fmeas', { state: { type: 'fmea', id: item.id } })}
                                                className={`
                                                bg-primary text-white rounded-full 
                                                w-5 h-5 text-[9px] font-bold 
                                                flex items-center justify-center 
                                                hover:bg-secondary hover:z-10 hover:scale-125 transition-all shadow-sm
                                                ${isHighlighted ? 'animate-highlight ring-2 ring-offset-1 ring-primary' : ''}
                                            `}
                                            >
                                                {sortedRanks[item.id]}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className="flex justify-center mt-2">
                <span className="text-sm font-bold tracking-wider text-text-secondary">{xAxisLabel}</span>
            </div>
        </Card>
    );
};

// Risk Level Legend Component
const RiskLegend: React.FC<{ levels: RiskLevel[]; standardName: string }> = ({ levels, standardName }) => {
    return (
        <Card>
            <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-primary" />
                <h3 className="font-bold text-text-primary">Risk Level Legend - {standardName}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {levels.map((level, idx) => (
                    <div
                        key={idx}
                        className={`p-3 rounded-lg border ${level.bgClass} ${level.borderClass}`}
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <span className={`w-3 h-3 rounded-full ${level.color === 'red' ? 'bg-red-500' : level.color === 'orange' ? 'bg-orange-500' : level.color === 'yellow' ? 'bg-yellow-500' : 'bg-green-500'}`}></span>
                            <span className={`font-bold ${level.textClass}`}>{level.name}</span>
                        </div>
                        <p className={`text-xs ${level.textClass} opacity-80`}>{level.description}</p>
                    </div>
                ))}
            </div>
        </Card>
    );
};


const RiskAnalysis: React.FC = () => {
    const { allData, activeProject } = useAppContext();
    const [filterMode, setFilterMode] = useState<FilterMode>('all');

    // Get the current standard configuration
    const fmeaStandard = allData.projectMetadata?.fmeaStandard as FmeaStandard || 'AIAG_VDA';
    const standardConfig = useMemo(() => getStandardConfig(fmeaStandard), [fmeaStandard]);
    const riskConfig = standardConfig.riskMatrix;
    const levels = riskConfig.levels;

    // Helper: Is item in active project?
    const isItemInProject = (projName?: string, epochId?: number) => {
        if (activeProject === 'all') return true;
        const activeProjectNormalized = normalizeProjectName(activeProject);

        // 1. Direct Project check (Metadata Standards v2.0.0)
        if (projName) {
            return normalizeProjectName(projName) === activeProjectNormalized;
        }

        // 2. Fallback: Find the epoch this item belongs to
        if (!epochId) return false;
        const epoch = allData.iterationHistory?.find(e =>
            e.epochNumber === epochId || e.iteration === epochId
        );
        if (!epoch) return false;

        const epochName = epoch.epochName || '';
        let projectName = 'Other';
        if (epochName.startsWith('Magnus Genesis')) projectName = 'Magnus Genesis';
        else if (epochName.includes(' - ')) projectName = epochName.split(' - ')[0].trim();
        else projectName = epochName.trim();

        return normalizeProjectName(projectName) === activeProjectNormalized;
    };

    // 1. Filter Data
    const filteredItems = useMemo(() => {
        return allData.fmeaItems.filter(item => {
            // Project Filter
            if (!isItemInProject(item.projectName, item.epochId)) return false;

            // Status Filter
            if (filterMode === 'all') return true;
            const status = item.status || 'active';
            return status === filterMode;
        });
    }, [allData.fmeaItems, filterMode, activeProject, allData.iterationHistory]);

    // 2. Prepare and Count Data for Primary Matrix (S x O)
    const { primaryData, primaryCounts } = useMemo(() => {
        const data: RiskDataPoint[] = [];
        const counts: Record<number, number> = {};
        levels.forEach((_, idx) => counts[idx] = 0);

        filteredItems.forEach(item => {
            const riskLevelIndex = riskConfig.getRiskLevel(item.S, item.O, item.D);
            const point = {
                x: item.O,
                y: item.S,
                id: item.id,
                rpn: item.RPN || (item.S * item.O * item.D),
                riskLevelIndex
            };
            data.push(point);
            counts[riskLevelIndex] = (counts[riskLevelIndex] || 0) + 1;
        });

        return { primaryData: data, primaryCounts: counts };
    }, [filteredItems, levels, riskConfig]);

    // 3. Prepare and Count Data for Secondary Matrix (S x D) - only if shown
    const { secondaryData, secondaryCounts } = useMemo(() => {
        if (!riskConfig.showSecondaryMatrix) {
            return { secondaryData: [], secondaryCounts: {} };
        }

        const data: RiskDataPoint[] = [];
        const counts: Record<number, number> = {};
        levels.forEach((_, idx) => counts[idx] = 0);

        filteredItems.forEach(item => {
            // For S x D matrix, we use D as X axis
            const riskLevelIndex = riskConfig.getRiskLevel(item.S, item.O, item.D);
            const point = {
                x: item.D,
                y: item.S,
                id: item.id,
                rpn: item.RPN || (item.S * item.O * item.D),
                riskLevelIndex
            };
            data.push(point);
            counts[riskLevelIndex] = (counts[riskLevelIndex] || 0) + 1;
        });

        return { secondaryData: data, secondaryCounts: counts };
    }, [filteredItems, levels, riskConfig]);

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-text-primary">Risk Analysis Matrices</h1>
                    <p className="text-text-secondary mt-1">
                        <span className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                            <Info size={14} />
                            {standardConfig.name}
                        </span>
                        <span className="ml-2 text-sm">
                            Visualizing risk using {standardConfig.riskModel === 'AP' ? 'Action Priority' : 'Risk Priority Number'} model
                        </span>
                    </p>
                </div>
                <ActiveProjectBadge />
            </div>

            {/* Risk Level Legend */}
            <RiskLegend levels={levels} standardName={standardConfig.name} />

            <div className="flex items-center gap-2">
                <ModuleInfoModal
                    title={`Risk Analysis - ${standardConfig.name}`}
                    description={`Visualize your risk landscape using the ${standardConfig.name} standard. ${riskConfig.thresholdInfo}`}
                    benefits={[
                        `Color-coded risk zones per ${standardConfig.name} requirements.`,
                        "Compare 'Draft' vs. 'Released' risk profiles.",
                        "Click ranked items to navigate to FMEA details."
                    ]}
                    usageInstructions={[
                        "Use the filter buttons (Released, Draft, All) to change the dataset.",
                        "Hover over cells to see risk level descriptions.",
                        "Click a point to jump to the FMEA editor."
                    ]}
                />
                {/* Filter Controls */}
                <div className="flex items-center p-1 bg-card border border-border rounded-lg shadow-sm">
                    <button
                        onClick={() => setFilterMode('active')}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all ${filterMode === 'active' ? 'bg-green-100 text-green-700 shadow-sm' : 'text-text-secondary hover:bg-background'
                            }`}
                    >
                        <Files size={16} /> Released
                    </button>
                    <button
                        onClick={() => setFilterMode('draft')}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all ${filterMode === 'draft' ? 'bg-blue-100 text-blue-700 shadow-sm' : 'text-text-secondary hover:bg-background'
                            }`}
                    >
                        <FileText size={16} /> Draft
                    </button>
                    <button
                        onClick={() => setFilterMode('all')}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all ${filterMode === 'all' ? 'bg-gray-100 text-gray-700 shadow-sm' : 'text-text-secondary hover:bg-background'
                            }`}
                    >
                        <Layers size={16} /> All
                    </button>
                </div>
            </div>

            <div className={`grid gap-6 ${riskConfig.showSecondaryMatrix ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 max-w-2xl'}`}>
                <RiskMatrix
                    title={riskConfig.primaryTitle}
                    xAxisLabel={riskConfig.xAxis.label}
                    yAxisLabel={riskConfig.yAxis.label}
                    data={primaryData}
                    riskCounts={primaryCounts}
                    riskConfig={riskConfig}
                    levels={levels}
                />
                {riskConfig.showSecondaryMatrix && riskConfig.secondaryTitle && (
                    <RiskMatrix
                        title={riskConfig.secondaryTitle}
                        xAxisLabel="Detection (D)"
                        yAxisLabel={riskConfig.yAxis.label}
                        data={secondaryData}
                        riskCounts={secondaryCounts}
                        riskConfig={riskConfig}
                        levels={levels}
                    />
                )}
            </div>

            {/* Compliance Rules */}
            {standardConfig.complianceRules.length > 0 && (
                <Card>
                    <h3 className="font-bold text-text-primary mb-3 flex items-center gap-2">
                        <Info size={16} className="text-primary" />
                        {standardConfig.name} Compliance Rules
                    </h3>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {standardConfig.complianceRules.map((rule, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-text-secondary">
                                <span className="text-primary font-bold">•</span>
                                {rule}
                            </li>
                        ))}
                    </ul>
                </Card>
            )}
        </div>
    );
};

export default RiskAnalysis;
